import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLang } from "../i18n";
import { initServiceMap } from "./serviceMapRuntime";
import "./ServiceMap.css";

const FONT_ID = "service-map-fonts";

function ServiceMapView({ view }) {
  const rootRef = useRef(null);
  const runtimeRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { lang } = useLang();
  const langRef = useRef(lang);
  langRef.current = lang;
  const station = view === "map" ? searchParams.get("station") || "" : "";

  useEffect(() => {
    if (!document.getElementById(FONT_ID)) {
      const link = document.createElement("link");
      link.id = FONT_ID;
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans+JP:wght@400;500;600&family=Zen+Old+Mincho:wght@400;600&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    if (!rootRef.current) return undefined;
    const runtime = initServiceMap(rootRef.current, {
      view,
      lang: langRef.current,
      station,
      onNavigate: navigate,
    });
    runtimeRef.current = runtime;
    return () => {
      runtimeRef.current = null;
      runtime.destroy();
    };
  }, [view, station, navigate]);

  useEffect(() => {
    runtimeRef.current?.setLanguage(lang);
  }, [lang]);

  return (
    <div className="pt-16" data-testid={view === "map" ? "service-map-page" : "service-index-page"}>
      <div id="rb-map" data-lang="en" ref={rootRef}>
        <a className="rbm-skip" href="#rbm-start" data-i18n="skip">Skip to the map</a>

        <header className="rbm-header" hidden>
          <div className="rbm-header-inner">
            <a className="rbm-brand" href="#rb-map" aria-label="Ruten Borealis">
              <span>Ruten Borealis</span>
              <small>架け橋 Kakehashi</small>
            </a>
            <nav className="rbm-nav" aria-label="Main navigation — services by line" data-nav-root></nav>
            <div className="rbm-actions">
              <button className="rbm-lang" type="button" data-lang-toggle>日本語</button>
              <a className="rbm-start" href="#rbm-start" data-start data-i18n="startHere">Start here</a>
            </div>
          </div>
        </header>

        <main>
          <section className="rbm-intro" aria-labelledby="rbm-title">
            <div className="rbm-content">
              <p className="rbm-eyebrow" data-i18n="eyebrow">Service map</p>
              <h1 id="rbm-title" data-i18n="title">Five lines. One way across.</h1>
              <p className="rbm-lede" data-i18n="lede"></p>
              <div className="rbm-views" role="group" aria-label="View">
                <button type="button" data-view="map" aria-pressed="true" data-i18n="tabMap">Map</button>
                <button type="button" data-view="line" aria-pressed="false" data-i18n="tabLine">Line</button>
                <button type="button" data-view="az" aria-pressed="false" data-i18n="tabAZ">A–Z index</button>
              </div>
            </div>
          </section>

          <section className="rbm-azview" id="rbm-az" data-azview hidden aria-labelledby="rbm-az-title">
            <div className="rbm-content">
              <div className="rbm-sechead">
                <h2 id="rbm-az-title" data-i18n="azTitle">Every service, A to Z</h2>
                <p data-az-count></p>
              </div>
              <nav className="rbm-azjump" aria-label="Jump to" data-az-jump></nav>
              <div className="rbm-azgrid" data-az-list></div>
            </div>
          </section>

          <section className="rbm-mapsec" id="rbm-start" data-mapview aria-labelledby="rbm-iam">
            <div className="rbm-content">
              <div className="rbm-entry">
                <p className="rbm-mono rbm-iam" id="rbm-iam" data-i18n="iam">Start here — I am…</p>
                <div className="rbm-entry-chips" role="group" aria-labelledby="rbm-iam" data-entry></div>
              </div>

              <div className="rbm-legend">
                <div className="rbm-legend-lines" role="group" aria-label="Lines" data-legend></div>
                <div className="rbm-key" aria-hidden="true">
                  <span><svg width="18" height="18" viewBox="0 0 18 18"><circle cx="9" cy="9" r="5.5" fill="#FCFAF5" stroke="#1B1917" strokeWidth="2.5"/></svg><b data-i18n="keyStation">Station</b></span>
                  <span><svg width="22" height="22" viewBox="0 0 22 22"><circle cx="11" cy="11" r="8.5" fill="#FCFAF5" stroke="#1B1917" strokeWidth="4"/></svg><b data-i18n="keyInter">Interchange</b></span>
                  <span><svg width="30" height="10" viewBox="0 0 30 10"><path d="M1 5H29" stroke="#1B1917" strokeWidth="4" strokeLinecap="round"/></svg><b data-i18n="keyRoute">Your route</b></span>
                </div>
              </div>

              <div className="rbm-mapwrap">
                <svg className="rbm-map" data-map viewBox="0 0 1510 910" role="group" aria-label="Service map"></svg>
                <div className="rbm-zoom" role="group" aria-label="Map zoom">
                  <button type="button" data-zoom="in" aria-label="Zoom in">+</button>
                  <button type="button" data-zoom="out" aria-label="Zoom out">−</button>
                </div>
                <p className="rbm-keynote" data-i18n="keyNote"></p>
              </div>

              <div className="rbm-panels">
                <section className="rbm-panel rbm-detail" aria-live="polite" data-detail></section>
                <section className="rbm-panel rbm-route" aria-live="polite" data-route></section>
              </div>
            </div>
          </section>

          <section className="rbm-lineview" data-lineview hidden aria-labelledby="rbm-lineview-title">
            <div className="rbm-content">
              <div className="rbm-sechead">
                <h2 id="rbm-lineview-title" data-i18n="lineViewTitle">Follow the line</h2>
                <p data-i18n="lineViewLede"></p>
              </div>
              <div className="rbm-lvpicker" role="group" aria-label="Lines" data-lvpicker></div>
              <div data-lvlist></div>
            </div>
          </section>

          <section className="rbm-linesec" data-mapview aria-labelledby="rbm-lines-title">
            <div className="rbm-content">
              <div className="rbm-sechead">
                <h2 id="rbm-lines-title" data-i18n="linesTitle">The lines</h2>
                <p data-i18n="linesLede"></p>
              </div>
              <div className="rbm-cards" data-cards></div>
            </div>
          </section>

          <section className="rbm-shelfsec" data-mapview aria-labelledby="rbm-shelf-title">
            <div className="rbm-content">
              <div className="rbm-sechead">
                <h2 id="rbm-shelf-title" data-i18n="shelfTitle">The shelf</h2>
                <p data-i18n="shelfLede"></p>
              </div>
              <div className="rbm-shelf" data-shelf></div>
            </div>
          </section>
          <section className="rbm-azsec" data-mapview>
            <div className="rbm-content rbm-az">
              <p data-i18n="azLede">Looking for one service by name?</p>
              <button type="button" className="rbm-azbtn" data-view="az" data-i18n="azLink">Open the A–Z index →</button>
            </div>
          </section>
        </main>

        <dialog className="rbm-dialog" data-dialog aria-labelledby="rbm-dlg-title">
          <div className="rbm-dialog-bar">
            <span className="rbm-mono" id="rbm-dlg-title" data-dlg-title></span>
            <button type="button" className="rbm-close" data-close data-i18n="close">Close</button>
          </div>
          <div data-dlg-body></div>
        </dialog>
      </div>
    </div>
  );
}

export function ServiceIndexPage() {
  return <ServiceMapView view="az" />;
}

export default function ServiceMapPage() {
  return <ServiceMapView view="map" />;
}
